<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import WootMessageEditor from '../../../../components/widgets/WootWriter/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import Toggle from './Toggle.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import { emitter } from 'shared/helpers/mitt';
import { serializeMessageTemplate } from '../helpers/messageTemplateSerializer';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({}),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved', 'close', 'dirtyChange']);
const { t } = useI18n();
const store = useStore();
const currentAccountId = computed(
  () =>
    store.getters.getCurrentAccountId ??
    store.getters.getCurrentAccount?.id ??
    null
);
let accountGeneration = 0;
let fetchGeneration = 0;
let saveGeneration = 0;
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;

const formRef = ref(null);
const discardConfirmation = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref(false);
const errors = ref({});

const clone = value => JSON.parse(JSON.stringify(value));
const ensureString = value =>
  value === null || value === undefined ? '' : String(value);

const buildInitialFormData = () => ({
  pipeline_id: ensureString(props.initialData.pipeline_id),
  stage_id: ensureString(props.initialData.stage_id),
  title: props.initialData.title || '',
  content: props.initialData.content || '',
  category: props.initialData.category || 'other',
  conditions: {
    enabled: Boolean(props.initialData.conditions?.enabled),
    rules: clone(props.initialData.conditions?.rules || []),
  },
});

const formData = ref(buildInitialFormData());
const initialSnapshot = ref(JSON.stringify(formData.value));

const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const selectedPipeline = computed(() =>
  pipelines.value.find(
    pipeline => ensureString(pipeline.id) === formData.value.pipeline_id
  )
);
const pipelineOptions = computed(() =>
  pipelines.value.map(pipeline => ({
    value: ensureString(pipeline.id),
    label: pipeline.name,
  }))
);
const pipelineStages = computed(() =>
  Object.entries(selectedPipeline.value?.stages || {})
    .map(([id, stage]) => ({
      value: ensureString(id),
      label: stage.name,
      position: Number.parseInt(stage.position, 10) || 0,
    }))
    .sort((left, right) => left.position - right.position)
);
const categoryOptions = computed(() => [
  {
    value: 'follow-up',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.FOLLOW_UP'),
  },
  {
    value: 'proposal',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.PROPOSAL'),
  },
  {
    value: 'negotiation',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.NEGOTIATION'),
  },
  {
    value: 'closing',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.CLOSING'),
  },
  {
    value: 'support',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.SUPPORT'),
  },
  {
    value: 'other',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.OTHER'),
  },
]);
const operatorOptions = computed(() => [
  { value: 'equals', label: t('PIPELINE.TEMPLATE_RULES.OP_EQUALS') },
  { value: 'contains', label: t('PIPELINE.TEMPLATE_RULES.OP_CONTAINS') },
  {
    value: 'starts_with',
    label: t('PIPELINE.TEMPLATE_RULES.OP_STARTS_WITH'),
  },
  {
    value: 'ends_with',
    label: t('PIPELINE.TEMPLATE_RULES.OP_ENDS_WITH'),
  },
]);
const availableFields = computed(() =>
  [
    { value: 'item_details.title', label: t('PIPELINE.VARIABLES.TITLE') },
    {
      value: 'item_details.description',
      label: t('PIPELINE.VARIABLES.DESCRIPTION'),
    },
    {
      value: 'item_details.priority',
      label: t('PIPELINE.VARIABLES.PRIORITY'),
    },
    { value: 'item_details.agent_id', label: t('PIPELINE.VARIABLES.AGENT') },
    {
      value: 'item_details.conversation_id',
      label: t('PIPELINE.VARIABLES.CONVERSATION'),
    },
    { value: 'item_details.value', label: t('PIPELINE.VARIABLES.VALUE') },
    { value: 'pipeline_stage', label: t('PIPELINE.STAGE.LABEL') },
  ].sort((left, right) => left.label.localeCompare(right.label))
);

const isDirty = computed(
  () => JSON.stringify(formData.value) !== initialSnapshot.value
);

watch(isDirty, value => emit('dirtyChange', value), { immediate: true });

const selectFirstAvailableStage = () => {
  const stageExists = pipelineStages.value.some(
    stage => stage.value === formData.value.stage_id
  );
  if (!stageExists)
    formData.value.stage_id = pipelineStages.value[0]?.value || '';
};

watch(
  () => formData.value.pipeline_id,
  () => {
    selectFirstAvailableStage();
    errors.value.pipeline = '';
    errors.value.stage = '';
  }
);

const fetchPipelines = async () => {
  if (isLoading.value) return;

  const accountContext = captureAccountContext();
  fetchGeneration += 1;
  const requestGeneration = fetchGeneration;
  const requestIsCurrent = () =>
    requestGeneration === fetchGeneration &&
    accountContextIsCurrent(accountContext);
  if (accountContext.accountId === null) {
    loadError.value = true;
    return;
  }

  isLoading.value = true;
  loadError.value = false;
  try {
    await store.dispatch('pipelines/fetch', {
      force: true,
      accountId: accountContext.accountId,
      generation: requestGeneration,
    });
    if (!requestIsCurrent()) return;
    if (!formData.value.pipeline_id && pipelines.value.length) {
      formData.value.pipeline_id = ensureString(pipelines.value[0].id);
    }
    selectFirstAvailableStage();
    initialSnapshot.value = JSON.stringify(formData.value);
  } catch {
    if (requestIsCurrent()) loadError.value = true;
  } finally {
    if (requestIsCurrent()) isLoading.value = false;
  }
};

const focusFirstInvalidField = async () => {
  await nextTick();
  const fieldOrder = [
    ['pipeline', '[name="pipeline_id"]'],
    ['stage', '[name="stage_id"]'],
    ['title', '#message-template-title'],
    ['conditions', '[data-condition-field]'],
    ['content', '[contenteditable="true"]'],
  ];
  const invalidField = fieldOrder.find(([key]) => errors.value[key]);
  formRef.value?.querySelector(invalidField?.[1])?.focus();
};

const validateForm = () => {
  const nextErrors = {};

  if (!selectedPipeline.value) {
    nextErrors.pipeline = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.PIPELINE_REQUIRED'
    );
  }
  if (
    !formData.value.stage_id ||
    !pipelineStages.value.some(stage => stage.value === formData.value.stage_id)
  ) {
    nextErrors.stage = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.STAGE_REQUIRED'
    );
  }
  if (!formData.value.title.trim()) {
    nextErrors.title = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.TITLE_REQUIRED'
    );
  }
  if (!formData.value.content.trim()) {
    nextErrors.content = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.CONTENT_REQUIRED'
    );
  }
  if (
    formData.value.conditions.enabled &&
    (!formData.value.conditions.rules.length ||
      formData.value.conditions.rules.some(
        rule => !rule.field || !rule.operator || !String(rule.value).trim()
      ))
  ) {
    nextErrors.conditions = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.CONDITIONS_INVALID'
    );
  }

  errors.value = nextErrors;
  if (Object.keys(nextErrors).length) focusFirstInvalidField();
  return Object.keys(nextErrors).length === 0;
};

const createTemplateId = templates => {
  let candidate = Date.now();
  const existingIds = new Set(templates.map(template => String(template?.id)));
  while (existingIds.has(String(candidate))) candidate += 1;
  return candidate;
};

const handleSubmit = async () => {
  if (isSaving.value || !validateForm()) return;

  const currentPipeline = selectedPipeline.value;
  if (!currentPipeline) return;
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;
  saveGeneration += 1;
  const requestGeneration = saveGeneration;
  const requestIsCurrent = () =>
    requestGeneration === saveGeneration &&
    accountContextIsCurrent(accountContext);

  isSaving.value = true;
  errors.value.submit = '';
  try {
    const updatedStages = clone(currentPipeline.stages || {});
    const targetStage = updatedStages[formData.value.stage_id];
    if (!targetStage) throw new Error('stage_not_found');

    if (props.isEditing) {
      const originalStageId = ensureString(props.initialData.stage_id);
      const originalStage = updatedStages[originalStageId];
      if (!originalStage) throw new Error('original_stage_not_found');

      const originalTemplates = Array.isArray(originalStage.message_templates)
        ? originalStage.message_templates
        : [];
      const originalIndex = originalTemplates.findIndex(
        template => String(template.id) === String(props.initialData.id)
      );
      if (originalIndex < 0) throw new Error('template_not_found');
      originalTemplates.splice(originalIndex, 1);
      originalStage.message_templates = originalTemplates;
    }

    const targetTemplates = Array.isArray(targetStage.message_templates)
      ? targetStage.message_templates
      : [];
    const normalizedTemplate = serializeMessageTemplate(
      {
        id: props.isEditing
          ? props.initialData.id
          : createTemplateId(targetTemplates),
        title: formData.value.title.trim(),
        content: formData.value.content.trim(),
        category: formData.value.category,
        conditions: clone(formData.value.conditions),
        pipeline_id: formData.value.pipeline_id,
        stage_id: formData.value.stage_id,
        ...(props.isEditing
          ? { updated_at: new Date().toISOString() }
          : { created_at: new Date().toISOString() }),
      },
      {
        existingTemplate: props.initialData,
        preserveLegacyWebhook: props.isEditing,
      }
    );
    targetStage.message_templates = [...targetTemplates, normalizedTemplate];

    await PipelinesAPI.update(
      currentPipeline.id,
      { stages: updatedStages },
      accountContext.accountId
    );
    if (!requestIsCurrent()) return;
    await store.dispatch('pipelines/fetch', {
      force: true,
      accountId: accountContext.accountId,
      generation: requestGeneration,
    });
    if (!requestIsCurrent()) return;
    initialSnapshot.value = JSON.stringify(formData.value);
    emitter.emit('newToastMessage', {
      message: props.isEditing
        ? t('PIPELINE.MESSAGE_TEMPLATES.UPDATE_SUCCESS')
        : t('PIPELINE.MESSAGE_TEMPLATES.CREATE_SUCCESS'),
      action: { type: 'success' },
    });
    emit('saved', normalizedTemplate);
  } catch {
    if (!requestIsCurrent()) return;
    errors.value.submit = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.SAVE_ERROR'
    );
    emitter.emit('newToastMessage', {
      message: errors.value.submit,
      action: { type: 'error' },
    });
  } finally {
    if (requestIsCurrent()) isSaving.value = false;
  }
};

const addConditionRule = () => {
  formData.value.conditions.rules.push({
    field: '',
    operator: 'equals',
    value: '',
  });
};

const removeConditionRule = index => {
  formData.value.conditions.rules.splice(index, 1);
};

const requestClose = async () => {
  if (isSaving.value) return false;
  if (isDirty.value) {
    const confirmed = await discardConfirmation.value.showConfirmation();
    if (!confirmed) return false;
  }
  emit('close');
  return true;
};

defineExpose({ requestClose, isDirty });

onMounted(fetchPipelines);

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    fetchGeneration += 1;
    saveGeneration += 1;
    isLoading.value = false;
    isSaving.value = false;
    loadError.value = false;
    errors.value = {};
    fetchPipelines();
  },
  { flush: 'sync' }
);
</script>

<template>
  <form
    ref="formRef"
    class="flex min-h-0 flex-col gap-5"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <div class="pr-12">
      <h2 data-modal-title class="text-xl font-semibold text-n-slate-12">
        {{
          isEditing
            ? t('PIPELINE.MESSAGE_TEMPLATES.EDIT_TITLE')
            : t('PIPELINE.MESSAGE_TEMPLATES.NEW')
        }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="isLoading"
      role="status"
      aria-live="polite"
      class="flex min-h-48 items-center justify-center gap-3 text-sm text-n-slate-11"
    >
      <span
        aria-hidden="true"
        class="size-6 animate-spin rounded-full border-2 border-n-weak border-t-n-brand motion-reduce:animate-none"
      />
      {{ t('PIPELINE.LOADING_PIPELINES') }}
    </div>

    <div
      v-else-if="loadError"
      role="alert"
      class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-6 text-center"
    >
      <p class="text-sm text-n-ruby-11">
        {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.LOAD_ERROR') }}
      </p>
      <Button
        type="button"
        variant="outline"
        color="slate"
        size="md"
        @click="fetchPipelines"
      >
        {{ t('PIPELINE.MESSAGE_TEMPLATES.RETRY') }}
      </Button>
    </div>

    <template v-else>
      <div
        class="grid min-h-0 grid-cols-1 gap-5 lg:grid-cols-[minmax(18rem,0.8fr)_minmax(22rem,1.2fr)]"
      >
        <div class="space-y-5">
          <section
            class="space-y-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.PIPELINE_INFO') }}
            </h3>

            <div
              class="space-y-1 [&>div]:w-full [&_select]:min-h-11 [&_select]:w-full"
            >
              <label
                for="message-template-pipeline"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.PIPELINE.LABEL') }}
              </label>
              <Select
                v-model="formData.pipeline_id"
                input-id="message-template-pipeline"
                name="pipeline_id"
                :options="pipelineOptions"
                :disabled="isEditing"
                :placeholder="
                  t('PIPELINE.MESSAGE_TEMPLATES.FORM.PIPELINE.PLACEHOLDER')
                "
                :error="errors.pipeline"
              />
              <p
                v-if="errors.pipeline"
                role="alert"
                class="text-xs text-n-ruby-9"
              >
                {{ errors.pipeline }}
              </p>
            </div>

            <div
              class="space-y-1 [&>div]:w-full [&_select]:min-h-11 [&_select]:w-full"
            >
              <label
                for="message-template-stage"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.STAGE.LABEL') }}
              </label>
              <Select
                v-model="formData.stage_id"
                input-id="message-template-stage"
                name="stage_id"
                :options="pipelineStages"
                :placeholder="
                  t('PIPELINE.MESSAGE_TEMPLATES.FORM.STAGE.PLACEHOLDER')
                "
                :error="errors.stage"
              />
              <p v-if="errors.stage" role="alert" class="text-xs text-n-ruby-9">
                {{ errors.stage }}
              </p>
            </div>

            <div
              class="space-y-1 [&>div]:w-full [&_select]:min-h-11 [&_select]:w-full"
            >
              <label
                for="message-template-category"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.CATEGORY') }}
              </label>
              <Select
                v-model="formData.category"
                input-id="message-template-category"
                name="category"
                :options="categoryOptions"
              />
            </div>
          </section>

          <section
            class="space-y-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.BASIC_INFO') }}
            </h3>
            <Input
              id="message-template-title"
              v-model="formData.title"
              :label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.TITLE.LABEL')"
              :placeholder="
                t('PIPELINE.MESSAGE_TEMPLATES.FORM.TITLE.PLACEHOLDER')
              "
              :message="errors.title"
              :message-type="errors.title ? 'error' : 'info'"
            />
          </section>

          <section
            class="space-y-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div class="flex min-h-11 items-center justify-between gap-4">
              <div>
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.TITLE') }}
                </h3>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.DESCRIPTION')
                  }}
                </p>
              </div>
              <Toggle
                v-model="formData.conditions.enabled"
                role="switch"
                :aria-checked="formData.conditions.enabled"
                :aria-label="
                  t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.TOGGLE_LABEL')
                "
              />
            </div>

            <div v-if="formData.conditions.enabled" class="space-y-3">
              <div
                v-for="(rule, index) in formData.conditions.rules"
                :key="index"
                class="grid grid-cols-1 gap-3 rounded-lg border border-n-weak p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_10rem_minmax(0,1fr)_2.75rem]"
              >
                <div
                  class="space-y-1 [&>div]:w-full [&_select]:min-h-11 [&_select]:w-full"
                >
                  <label
                    :for="'message-template-rule-field-' + index"
                    class="text-xs font-medium text-n-slate-11"
                  >
                    {{
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.LABEL'
                      )
                    }}
                  </label>
                  <Select
                    v-model="rule.field"
                    :input-id="'message-template-rule-field-' + index"
                    data-condition-field
                    :options="availableFields"
                    :placeholder="
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.PLACEHOLDER'
                      )
                    "
                  />
                </div>
                <div
                  class="space-y-1 [&>div]:w-full [&_select]:min-h-11 [&_select]:w-full"
                >
                  <label
                    :for="'message-template-rule-operator-' + index"
                    class="text-xs font-medium text-n-slate-11"
                  >
                    {{
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATOR.LABEL'
                      )
                    }}
                  </label>
                  <Select
                    v-model="rule.operator"
                    :input-id="'message-template-rule-operator-' + index"
                    :options="operatorOptions"
                  />
                </div>
                <Input
                  :id="'message-template-rule-value-' + index"
                  v-model="rule.value"
                  :label="
                    t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.LABEL')
                  "
                  :placeholder="
                    t(
                      'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.PLACEHOLDER'
                    )
                  "
                />
                <Button
                  type="button"
                  variant="ghost"
                  color="ruby"
                  size="md"
                  icon="i-lucide-trash-2"
                  class="self-end"
                  :aria-label="
                    t(
                      'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.REMOVE_RULE',
                      { number: index + 1 }
                    )
                  "
                  @click="removeConditionRule(index)"
                />
              </div>

              <p
                v-if="errors.conditions"
                role="alert"
                class="text-xs text-n-ruby-9"
              >
                {{ errors.conditions }}
              </p>
              <Button
                type="button"
                variant="outline"
                color="slate"
                size="md"
                icon="i-lucide-plus"
                class="w-full"
                @click="addConditionRule"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.ADD_RULE') }}
              </Button>
            </div>
          </section>
        </div>

        <section
          class="flex min-h-[24rem] flex-col rounded-xl border border-n-weak bg-n-alpha-1 p-4 lg:sticky lg:top-0 lg:max-h-[calc(100dvh-12rem)]"
        >
          <div class="mb-3">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONTENT.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONTENT.DESCRIPTION') }}
            </p>
          </div>
          <div
            class="min-h-[20rem] flex-1 overflow-auto rounded-lg bg-n-solid-2 p-2 [&_.woot-editor]:min-h-[20rem] [&_.woot-editor]:rounded-lg [&_.woot-editor]:border [&_.woot-editor]:border-n-weak [&_.woot-editor]:bg-n-solid-2 [&_.woot-editor]:p-2"
          >
            <WootMessageEditor
              v-model="formData.content"
              :placeholder="
                t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONTENT.PLACEHOLDER')
              "
              :error="errors.content"
            />
          </div>
          <p
            v-if="errors.content"
            role="alert"
            class="mt-2 text-xs text-n-ruby-9"
          >
            {{ errors.content }}
          </p>
        </section>
      </div>

      <p
        v-if="errors.submit"
        role="alert"
        class="rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
      >
        {{ errors.submit }}
      </p>

      <div
        class="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-n-weak bg-n-solid-1 py-4 sm:flex-row sm:justify-end"
      >
        <Button
          type="button"
          variant="outline"
          color="slate"
          size="md"
          :disabled="isSaving"
          class="w-full sm:w-auto"
          @click="requestClose"
        >
          {{ t('PIPELINE.ACTIONS.CANCEL') }}
        </Button>
        <Button
          type="submit"
          variant="solid"
          color="blue"
          size="md"
          :is-loading="isSaving"
          :disabled="isSaving || isLoading"
          class="w-full sm:w-auto"
        >
          {{ t('PIPELINE.ACTIONS.SAVE') }}
        </Button>
      </div>
    </template>
  </form>

  <ConfirmModal
    ref="discardConfirmation"
    :title="t('PIPELINE.MESSAGE_TEMPLATES.FORM.DISCARD_TITLE')"
    :description="t('PIPELINE.MESSAGE_TEMPLATES.FORM.DISCARD_DESCRIPTION')"
    :confirm-label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.DISCARD_CONFIRM')"
    :cancel-label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.KEEP_EDITING')"
  />
</template>
